import clsx from 'clsx';
import React, { type ComponentPropsWithRef, type ElementType, type PropsWithChildren } from 'react';
import { Typography } from '../Typography';

type ButtonProps<T extends ElementType> = PropsWithChildren<{
  variant?: 'primary' | 'secondary' | 'outlined' | 'plain';
  component?: T;
  className?: string;
  href?: string;
  icon?: React.ReactNode;
  iconPosition?: 'start' | 'end';
  typographyClassname?: string;
  onClick?: (e: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => void;
}> &
  ComponentPropsWithRef<T>;

const primaryStyles = clsx(
  '!text-white',
  'bg-green-default',
  'hover:bg-green-dark',
  'text-accent-medium',
  'font-bold',
);

const secondaryStyles = clsx(
  'text-green-default',
  'bg-neutral-lightest',
  'hover:bg-neutral-lighter',
  'font-bold',
);

const outlinedStyles = clsx(
  'text-green-default',
  'bg-transparent',
  'outline-1',
  'outline-green-default',
  'outline',
  'hover:[box-shadow:inset_0_0_0_1px_#006642]',
  'font-bold',
);

const plainStyles = clsx('px-3', 'py-3');

const notPlainStyles = clsx('px-4', 'py-3', 'rounded-[.125rem]');

export function Button<T extends ElementType = 'button'>({
  variant = 'primary',
  component: Component = 'button',
  icon,
  iconPosition = 'start',
  typographyClassname,
  children,
  className,
  ...componentProps
}: ButtonProps<T>) {
  return (
    <Component
      className={clsx(
        'border-0',
        'no-underline',
        'cursor-pointer',
        'inline-block',
        'whitespace-nowrap',
        'relative',
        'text-center',
        'touch-manipulation',
        'min-w-min',
        'transition-colors',
        'duration-150',
        {
          [primaryStyles]: variant === 'primary',
          [secondaryStyles]: variant === 'secondary',
          [outlinedStyles]: variant === 'outlined',
          [plainStyles]: variant === 'plain',
          [notPlainStyles]: variant !== 'plain',
        },
        className,
      )}
      {...componentProps}
    >
      <div
        className={clsx('opacity-100', 'transition-transform', 'duration-120', 'ease-linear', {
          flex: !!icon,
          'items-center': !!icon,
          'gap-2': !!icon,
        })}
      >
        {iconPosition === 'start' && icon}
        <Typography
          variant={variant === 'plain' ? 'text-medium' : 'accent-medium'}
          className={clsx(
            {
              'font-bold': variant !== 'plain',
            },
            typographyClassname,
          )}
        >
          {children}
        </Typography>
        {iconPosition === 'end' && icon}
      </div>
    </Component>
  );
}
